import React from 'react'
import styled from 'styled-components'
import { RouteComponentProps, useLocation } from 'react-router-dom'
import {
  DynamicLoadingPage,
  NotFoundView,
  usePreviewPwd,
} from '@sketch/modules-common'
import { PreviewLayout } from './PreviewLayout'
import { PageCanvasView } from './PageCanvasView'
import { PasswordProtectedScreen } from './PasswordProtectedScreen'
import { PreviewContextProvider } from './PreviewContext'
import { ShareViewsNavigationContextProvider } from '../../utils/shareViewsNavigation'
import ShareSidebarTabContext from '../../components/ShareSidebarTabContext'
import { useGetPreviewDetailsQuery } from '@sketch/gql-types'
import DocumentView from '../../DocumentView'
import { CanvasRendererProvider } from '@sketch-hq/sketch-web-renderer'
import { ReactComponent as MagnifyingGlass } from '@sketch/icons/document-magnifying-glass-64'
import { Text } from '@sketch/components'
import { AnnotationSubscriptions } from '../../../annotations/containers'

type PreviewViewProps = RouteComponentProps<{
  previewID: string
}>

const DocumentIcon = styled(MagnifyingGlass)`
  width: 64px;
  height: 64px;
  flex: none;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

const ConnectedAnnotationSubscriptions = ({
  previewIdentifier,
}: {
  previewIdentifier: string
}) => {
  return (
    <AnnotationSubscriptions
      // pass share and version ids as empty strings, we only want the previewIdentifier to go through
      shareIdentifier={''}
      versionIdentifier={''}
      previewIdentifier={previewIdentifier}
    />
  )
}

export const PreviewView: React.FC<PreviewViewProps> = ({ match }) => {
  const location = useLocation()

  const { previewID } = match.params

  const password = usePreviewPwd(previewID)

  const { loading, error, data } = useGetPreviewDetailsQuery({
    variables: {
      previewIdentifier: previewID,
      ...(password && { password }),
    },
  })

  if (loading) {
    return <DynamicLoadingPage />
  }

  const hasForbiddenError = error?.graphQLErrors?.some(
    error => error.extensions?.code === 'FORBIDDEN'
  )

  const preview = data?.preview

  if (hasForbiddenError && !preview) {
    return <PasswordProtectedScreen previewID={previewID} />
  }

  if (error || !preview) {
    return (
      <NotFoundView
        title="We cannot find this preview."
        additionalMessage={
          <Text.Span textStyle="copy.quaternary.standard.E" pb={4}>
            Please check the URL with the person that shared it with you. It
            might have been deleted or moved.
          </Text.Span>
        }
        customIcon={<DocumentIcon />}
        hideLink
      />
    )
  }

  const isPrototypesRoute = location.pathname.includes('/prototypes')
  const title = preview.name

  return (
    <ShareViewsNavigationContextProvider>
      <PreviewContextProvider preview={preview} password={password}>
        <ConnectedAnnotationSubscriptions previewIdentifier={previewID} />
        <ShareSidebarTabContext>
          <CanvasRendererProvider>
            <PreviewLayout
              title={title}
              render={layoutProps => {
                if (isPrototypesRoute) {
                  return <DocumentView {...layoutProps} />
                }

                return <PageCanvasView preview={preview} {...layoutProps} />
              }}
            />
          </CanvasRendererProvider>
        </ShareSidebarTabContext>
      </PreviewContextProvider>
    </ShareViewsNavigationContextProvider>
  )
}
